// Budget Category Types
export type CategoryType = 'income' | 'expense'
export type TaxRate = 'normal' | 'supplemental'
export type FilingStatus = 'single' | 'married'
export type VestingSchedule = 'immediate' | 'graded' | 'cliff'
export type RetirementContributionType = 'pre-tax' | 'post-tax'

export interface BudgetCategory {
  name: string
  amount: number
  inflationAdjusted: boolean
  type: CategoryType
  autoCalculated?: boolean
}

export interface CustomCategory extends BudgetCategory {
  id: number
}

export interface RetirementContribution {
  amount: number
  percentage: number
  max: number
  type: RetirementContributionType
  employerMatch: number
  employerMatchRate: number
}

export interface RetirementContributions {
  preTax401k: RetirementContribution
  roth401k: RetirementContribution
  traditionalIRA: RetirementContribution
  rothIRA: RetirementContribution
  hsa: RetirementContribution
  otherPreTax: RetirementContribution
  otherPostTax: RetirementContribution
}

export interface EmployerMatching {
  enabled: boolean
  matchRate: number
  matchLimit: number
  maxMatchAmount: number
  vestingSchedule: VestingSchedule
  vestingYears: number
}

export interface TaxSettings {
  userState: string
  filingStatus: FilingStatus
  itemizedDeductions: number
  otherDeductions: number
  otherIncome: number
  capitalGains: number
}

export interface IncomeType {
  name: string
  amount: number
  type: string
  taxRate: TaxRate
}

export interface IncomeTypes {
  salary: IncomeType
  bonus: IncomeType
  commission: IncomeType
  overtime: IncomeType
  sideIncome: IncomeType
  rentalIncome: IncomeType
  [key: string]: IncomeType
}

export interface WageGrowth {
  annualGrowthRatePct: number
  applyTo: string[]
  enabled: boolean
}

export interface BudgetCalculations {
  grossIncome: number
  adjustedGrossIncome: number
  taxableIncome: number
  federalTax: number
  stateTax: number
  totalTax: number
  netIncome: number
  totalExpenses: number
  freeCashFlow: number
  savingsRate: number
  totalRetirementContributions?: number
  employerMatchTotal?: number
  stateTaxRate?: number
}

export interface BudgetState {
  categories: Record<string, BudgetCategory>
  customCategories: CustomCategory[]
  retirementContributions: RetirementContributions
  employerMatching: EmployerMatching
  taxSettings: TaxSettings
  incomeTypes: IncomeTypes
  wageGrowth: WageGrowth
  calculations: BudgetCalculations
  _isUpdatingCalculations: boolean
}

// State codes for tax calculation
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
] as const

export type USState = typeof US_STATES[number]
